/**
 * The DOM side of dialogFocus.ts: give one of the app's own dialogs the
 * keyboard behaviour a HeroUI `Modal` has — focus inside on open, Escape to
 * close (not from a text field unless asked), Tab kept inside a modal one,
 * focus handed back on close.
 */
import { type RefObject, useEffect, useRef } from 'react';

import { dialogKeyAction, FOCUSABLE_SELECTOR, initialFocusChoice } from './dialogFocus.ts';

export interface UseDialogFocusOptions {
  /** Close on Escape. Leave out when the dialog already handles Escape itself. */
  onEscape?: () => void;
  /** Keep Tab inside (a modal dialog, with a backdrop). */
  trap?: boolean;
  /** The element to focus on open; default the first focusable one. */
  initialFocus?: RefObject<HTMLElement | null>;
  /** Escape closes even from a text field (a palette whose field is the whole dialog). */
  escapeFromFields?: boolean;
  /** Off while false (a popover that is closed). */
  enabled?: boolean;
}

function focusables(container: HTMLElement): HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)].filter((el) => !el.closest('[inert]') && !el.hasAttribute('disabled') && el.getClientRects().length > 0);
}

export function useDialogFocus(ref: RefObject<HTMLElement | null>, options: UseDialogFocusOptions = {}): void {
  const { trap = false, escapeFromFields = false, enabled = true } = options;
  const latest = useRef(options);
  useEffect(() => {
    latest.current = options;
  });

  useEffect(() => {
    if (!enabled) return;
    const container = ref.current;
    if (!container) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const list = focusables(container);
    const preferred = latest.current.initialFocus?.current ?? null;
    const choice = initialFocusChoice({ alreadyInside: container.contains(document.activeElement), hasPreferred: !!preferred, focusableCount: list.length });
    if (choice === 'preferred') preferred?.focus();
    else if (choice === 'first') list[0].focus();
    else if (choice === 'container') {
      if (!container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
      container.focus();
    }

    const onKey = (e: KeyboardEvent) => {
      // A key pressed in another dialog stacked above (a confirm, a menu) is that dialog's.
      if (e.defaultPrevented) return;
      if (e.key !== 'Escape' && e.key !== 'Tab') return;
      const target = e.target instanceof HTMLElement ? e.target : null;
      // Only keys from inside this dialog, or from nowhere in particular (the body).
      if (target && target !== document.body && !container.contains(target)) return;
      const items = focusables(container);
      const action = dialogKeyAction({
        key: e.key,
        shiftKey: e.shiftKey,
        targetTag: target?.tagName,
        targetEditable: !!target?.isContentEditable,
        index: target ? items.indexOf(target) : -1,
        count: items.length,
        closeOnEscape: !!latest.current.onEscape,
        trap,
        escapeFromFields,
      });
      if (action === 'none') return;
      e.preventDefault();
      if (action === 'close') latest.current.onEscape?.();
      else if (action === 'wrap-to-first') items[0]?.focus();
      else items[items.length - 1]?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      // Hand focus back, unless something else has deliberately taken it since.
      const now = document.activeElement;
      if (previous && previous.isConnected && (!now || now === document.body || container.contains(now) || !now.isConnected)) previous.focus();
    };
  }, [ref, trap, escapeFromFields, enabled]);
}
